import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Loader2,
  PanelLeft,
  PanelRight,
  PictureInPicture2,
  X,
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { EvaluationFiltersV2 } from "./EvaluationFiltersV2";
import type { EvaluationFilterValues } from "@/types/freelancer";
import {
  FacetMultiSelect,
  FieldLabel,
  FilterChipToggle,
  SegmentedFilter,
  type FilterOption,
} from "./filter-controls";
import {
  countActiveFilters,
  isSalaryBandAbove,
  sortSalaryValues,
  FilterValues,
  LINKEDIN_STATUS_OPTIONS,
  OPEN_TO_WORK_OPTIONS,
  PORTFOLIO_STATUS_OPTIONS,
  SALARY_BAND_CUTOFF_REAIS,
} from "./filterUtils";

/** Posição do painel: sobre o menu à esquerda, flutuante à direita ou encaixado. */
export type FiltersPanelMode = "left" | "floating" | "docked";

const MODE_OPTIONS: Array<{
  mode: FiltersPanelMode;
  icon: typeof PanelLeft;
  title: string;
}> = [
  { mode: "left", icon: PanelLeft, title: "Sobre o menu à esquerda" },
  { mode: "floating", icon: PictureInPicture2, title: "Flutuante à direita" },
  { mode: "docked", icon: PanelRight, title: "Encaixado ao lado da listagem" },
];

interface FreelancerFiltersPanelProps {
  filters: FilterValues;
  onFiltersChange: (next: FilterValues) => void;
  onClearAll: () => void;
  onClose: () => void;
  mode: FiltersPanelMode;
  onModeChange: (mode: FiltersPanelMode) => void;
  /** Slot do grupo Disponibilidade: os mesmos chips coloridos com contagem do panorama. */
  disponibilidadeContent: React.ReactNode;
  /** Total após busca + filtros (auto-apply: atualiza ao vivo). */
  resultCount?: number;
  isFetching?: boolean;
  className?: string;
}

type GroupId =
  | "disponibilidade"
  | "perfil"
  | "interesses"
  | "localizacao"
  | "avaliacao"
  | "admin";

const GROUP_KEYS: Record<GroupId, string[]> = {
  disponibilidade: [
    "disp_temperatura",
    "disp_interesse",
    "disp_emprego",
    "disp_relacao",
    "disponibilidade",
    "open_to_work",
  ],
  perfil: ["areas", "tipo_atuacao", "idiomas", "tags", "auto_tags", "pretensoes_salariais"],
  interesses: [
    "interesse_vaga_fixa",
    "interesse_presencial",
    "interesse_em_mudar_emprego",
  ],
  localizacao: ["city", "state", "country", "cidade"],
  avaliacao: [
    "min_media_projeto",
    "min_media_perfil",
    "min_maior_nota",
    "atributos_selecionados",
    "avaliado_perfil",
    "avaliado_projeto",
  ],
  admin: ["is_active", "quality_seal", "external_links", "falar_com_frila"],
};

const GROUP_TITLE: Record<GroupId, string> = {
  disponibilidade: "Disponibilidade",
  perfil: "Perfil",
  interesses: "Interesses",
  localizacao: "Localização",
  avaliacao: "Avaliação",
  admin: "Administrativo",
};

function isKeyActive(filters: FilterValues, key: string): boolean {
  const value = filters[key];
  if (value === undefined || value === null || value === "") return false;
  if (Array.isArray(value) && value.length === 0) return false;
  if (key === "is_active" && value === "true") return false;
  return true;
}

function countGroupActive(filters: FilterValues, group: GroupId): number {
  return GROUP_KEYS[group].filter((key) => isKeyActive(filters, key)).length;
}

function toOptions(values: string[]): FilterOption[] {
  return values.map((value) => ({ value, label: value }));
}

const BOOLEAN_OPTIONS: FilterOption[] = [
  { value: "true", label: "Sim" },
  { value: "false", label: "Não" },
];

export function FreelancerFiltersPanel({
  filters,
  onFiltersChange,
  onClearAll,
  onClose,
  mode,
  onModeChange,
  disponibilidadeContent,
  resultCount,
  isFetching = false,
  className,
}: FreelancerFiltersPanelProps) {
  const [openGroups, setOpenGroups] = useState<string[]>(() =>
    (Object.keys(GROUP_KEYS) as GroupId[]).filter(
      (group) => countGroupActive(filters, group) > 0,
    ),
  );

  // Facets (valores distintos por campo) — mesma query/staleTime da V1.
  const { data: facets } = useQuery({
    queryKey: ["freelancer-filter-facets"],
    staleTime: 60 * 60 * 1000,
    queryFn: async (): Promise<Record<string, string[]>> => {
      const { data, error } = await supabase.rpc(
        "get_freelancer_filter_facets" as never,
      );
      if (error) throw error;
      return (data ?? {}) as Record<string, string[]>;
    },
  });
  const pickFacet = (field: string): string[] => facets?.[field] ?? [];

  const { data: areas = [] } = useQuery({
    queryKey: ["areas-atuacao-filter", "active"],
    staleTime: 60 * 60 * 1000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("areas_atuacao")
        .select("nome")
        .eq("is_active", true);
      if (error) throw error;
      return (data ?? [])
        .map((row) => row.nome as string)
        .filter((nome): nome is string => typeof nome === "string" && nome.length > 0)
        .sort((first, second) => first.localeCompare(second, "pt-BR"));
    },
  });

  const pretensaoOptions = useMemo(
    () => toOptions(sortSalaryValues(pickFacet("pretensao_salarial"))),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [facets],
  );

  const arrayValue = (key: string): string[] =>
    Array.isArray(filters[key]) ? (filters[key] as string[]) : [];

  const pretensoesAcimaDoCorte = useMemo(
    () =>
      pretensaoOptions
        .map((option) => option.value)
        .filter((band) => isSalaryBandAbove(band, SALARY_BAND_CUTOFF_REAIS)),
    [pretensaoOptions],
  );
  const pretensoesSelecionadas = arrayValue("pretensoes_salariais");
  const acimaDoCorteAtivo =
    pretensoesAcimaDoCorte.length > 0 &&
    pretensoesSelecionadas.length === pretensoesAcimaDoCorte.length &&
    pretensoesAcimaDoCorte.every((band) => pretensoesSelecionadas.includes(band));
  const scalarValue = (key: string): string =>
    typeof filters[key] === "string" ? (filters[key] as string) : "";

  const setScalar = (key: string, value: string) => {
    onFiltersChange({ ...filters, [key]: value });
  };

  const setArray = (key: string, values: string[]) => {
    const next = { ...filters };
    if (values.length === 0) delete next[key];
    else next[key] = values;
    onFiltersChange(next);
  };

  const toggleArrayValue = (key: string, value: string) => {
    const current = arrayValue(key);
    setArray(
      key,
      current.includes(value)
        ? current.filter((item) => item !== value)
        : [...current, value],
    );
  };

  const evaluationFilters = filters as unknown as EvaluationFilterValues;
  const handleEvaluationChange = (next: EvaluationFilterValues) => {
    onFiltersChange({ ...filters, ...next } as FilterValues);
  };

  const totalActive = countActiveFilters(filters);

  return (
    <aside
      className={cn(
        "flex flex-col overflow-hidden rounded-lg border bg-background",
        className,
      )}
    >
      <div className="flex items-center gap-2 border-b px-4 py-2.5">
        <span className="text-sm font-semibold">Filtros</span>
        {totalActive > 0 && (
          <Badge variant="secondary" className="h-5 px-1.5 text-[11px]">
            {totalActive}
          </Badge>
        )}
        <div className="ml-auto flex items-center gap-1">
          <div className="mr-1 flex items-center rounded-md border p-0.5">
            {MODE_OPTIONS.map(({ mode: option, icon: Icon, title }) => (
              <button
                key={option}
                type="button"
                onClick={() => onModeChange(option)}
                title={title}
                aria-pressed={mode === option}
                className={cn(
                  "flex h-6 w-6 items-center justify-center rounded",
                  mode === option
                    ? "bg-primary/10 text-primary"
                    : "text-muted-foreground hover:bg-muted",
                )}
              >
                <Icon className="h-3.5 w-3.5" />
              </button>
            ))}
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            className="h-7 w-7"
            aria-label="Fechar filtros"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto px-4">
        <Accordion
          type="multiple"
          value={openGroups}
          onValueChange={setOpenGroups}
        >
          {/* ---------------- Perfil ---------------- */}
          <AccordionItem value="perfil">
            <GroupTrigger filters={filters} group="perfil" />
            <AccordionContent className="space-y-4">
              <div>
                <FieldLabel>Áreas de atuação</FieldLabel>
                <FacetMultiSelect
                  placeholder="Selecionar áreas..."
                  searchPlaceholder="Buscar área..."
                  options={toOptions(areas)}
                  selected={arrayValue("areas")}
                  onChange={(values) => setArray("areas", values)}
                />
              </div>
              <div>
                <FieldLabel>Tipo de atuação</FieldLabel>
                <SegmentedFilter
                  options={toOptions(pickFacet("tipo_atuacao"))}
                  value={scalarValue("tipo_atuacao")}
                  onChange={(value) => setScalar("tipo_atuacao", value)}
                />
              </div>
              <div>
                <FieldLabel>Pretensão salarial</FieldLabel>
                <FacetMultiSelect
                  placeholder="Selecionar pretensões..."
                  searchPlaceholder="Buscar valor..."
                  options={pretensaoOptions}
                  selected={pretensoesSelecionadas}
                  onChange={(values) => setArray("pretensoes_salariais", values)}
                />
                <div className="mt-1.5 flex flex-wrap gap-1">
                  <FilterChipToggle
                    active={acimaDoCorteAtivo}
                    onClick={() =>
                      setArray("pretensoes_salariais", acimaDoCorteAtivo ? [] : pretensoesAcimaDoCorte)
                    }
                  >
                    Acima de R$ {SALARY_BAND_CUTOFF_REAIS.toLocaleString("pt-BR")}
                  </FilterChipToggle>
                </div>
              </div>
              <div>
                <FieldLabel>Idiomas</FieldLabel>
                <FacetMultiSelect
                  placeholder="Selecionar idiomas..."
                  searchPlaceholder="Buscar idioma..."
                  options={toOptions(pickFacet("idiomas"))}
                  selected={arrayValue("idiomas")}
                  onChange={(values) => setArray("idiomas", values)}
                />
              </div>
              <div>
                <FieldLabel>Tags</FieldLabel>
                <FacetMultiSelect
                  placeholder="Selecionar tags..."
                  searchPlaceholder="Buscar tag..."
                  options={toOptions(pickFacet("tags"))}
                  selected={arrayValue("tags")}
                  onChange={(values) => setArray("tags", values)}
                />
              </div>
              <div>
                <FieldLabel>Tags automáticas (IA)</FieldLabel>
                <FacetMultiSelect
                  placeholder="Selecionar tags automáticas..."
                  searchPlaceholder="Buscar tag..."
                  options={toOptions(pickFacet("auto_tags"))}
                  selected={arrayValue("auto_tags")}
                  onChange={(values) => setArray("auto_tags", values)}
                />
              </div>
            </AccordionContent>
          </AccordionItem>

          {/* ---------------- Interesses ---------------- */}
          <AccordionItem value="interesses">
            <GroupTrigger filters={filters} group="interesses" />
            <AccordionContent className="space-y-4">
              <div>
                <FieldLabel>Interesse em vaga fixa</FieldLabel>
                <SegmentedFilter
                  options={toOptions(pickFacet("interesse_vaga_fixa"))}
                  value={scalarValue("interesse_vaga_fixa")}
                  onChange={(value) => setScalar("interesse_vaga_fixa", value)}
                />
              </div>
              <div>
                <FieldLabel>Interesse presencial</FieldLabel>
                <SegmentedFilter
                  options={toOptions(pickFacet("interesse_presencial"))}
                  value={scalarValue("interesse_presencial")}
                  onChange={(value) => setScalar("interesse_presencial", value)}
                />
              </div>
              <div>
                <FieldLabel>Quer mudar de emprego</FieldLabel>
                <SegmentedFilter
                  options={BOOLEAN_OPTIONS}
                  value={scalarValue("interesse_em_mudar_emprego")}
                  onChange={(value) =>
                    setScalar("interesse_em_mudar_emprego", value)
                  }
                />
              </div>
            </AccordionContent>
          </AccordionItem>

          {/* ---------------- Localização ---------------- */}
          <AccordionItem value="localizacao">
            <GroupTrigger filters={filters} group="localizacao" />
            <AccordionContent className="space-y-4">
              <div>
                <FieldLabel>Cidade</FieldLabel>
                <FacetMultiSelect
                  placeholder="Selecionar cidades..."
                  searchPlaceholder="Buscar cidade..."
                  options={toOptions(pickFacet("city"))}
                  selected={arrayValue("city")}
                  onChange={(values) => setArray("city", values)}
                />
              </div>
              <div>
                <FieldLabel>Estado</FieldLabel>
                <FacetMultiSelect
                  placeholder="Selecionar estados..."
                  searchPlaceholder="Buscar estado..."
                  options={toOptions(pickFacet("state"))}
                  selected={arrayValue("state")}
                  onChange={(values) => setArray("state", values)}
                />
              </div>
              <div>
                <FieldLabel>País</FieldLabel>
                <FacetMultiSelect
                  placeholder="Selecionar países..."
                  searchPlaceholder="Buscar país..."
                  options={toOptions(pickFacet("country"))}
                  selected={arrayValue("country")}
                  onChange={(values) => setArray("country", values)}
                />
              </div>
              <div>
                <FieldLabel>Endereço (campo livre do cadastro)</FieldLabel>
                <FacetMultiSelect
                  placeholder="Selecionar endereços..."
                  searchPlaceholder="Buscar endereço..."
                  options={toOptions(pickFacet("cidade"))}
                  selected={arrayValue("cidade")}
                  onChange={(values) => setArray("cidade", values)}
                />
              </div>
            </AccordionContent>
          </AccordionItem>

          {/* ---------------- Avaliação ---------------- */}
          <AccordionItem value="avaliacao">
            <GroupTrigger filters={filters} group="avaliacao" />
            <AccordionContent>
              <EvaluationFiltersV2
                filters={evaluationFilters}
                onFiltersChange={handleEvaluationChange}
              />
            </AccordionContent>
          </AccordionItem>

          {/* ---------------- Administrativo ---------------- */}
          <AccordionItem value="admin">
            <GroupTrigger filters={filters} group="admin" />
            <AccordionContent className="space-y-4">
              <div>
                <FieldLabel>Status do freelancer</FieldLabel>
                <SegmentedFilter
                  options={[
                    { value: "true", label: "Ativos" },
                    { value: "false", label: "Inativos" },
                  ]}
                  value={scalarValue("is_active")}
                  onChange={(value) => setScalar("is_active", value)}
                />
              </div>
              <div>
                <FieldLabel>Selo de qualidade</FieldLabel>
                <SegmentedFilter
                  options={[
                    { value: "true", label: "Verificado" },
                    { value: "false", label: "Não verificado" },
                  ]}
                  value={scalarValue("quality_seal")}
                  onChange={(value) => setScalar("quality_seal", value)}
                />
              </div>
              <div>
                <FieldLabel>LinkedIn</FieldLabel>
                <div className="flex flex-wrap gap-1">
                  {LINKEDIN_STATUS_OPTIONS.map((option) => (
                    <FilterChipToggle
                      key={option.value}
                      active={arrayValue("external_links").includes(option.value)}
                      onClick={() => toggleArrayValue("external_links", option.value)}
                    >
                      {option.label}
                    </FilterChipToggle>
                  ))}
                </div>
              </div>
              <div>
                <FieldLabel>Portfólio</FieldLabel>
                <div className="flex flex-wrap gap-1">
                  {PORTFOLIO_STATUS_OPTIONS.map((option) => (
                    <FilterChipToggle
                      key={option.value}
                      active={arrayValue("external_links").includes(option.value)}
                      onClick={() => toggleArrayValue("external_links", option.value)}
                    >
                      {option.label}
                    </FilterChipToggle>
                  ))}
                </div>
              </div>
              <div>
                <FieldLabel>Falar com o Frila</FieldLabel>
                <SegmentedFilter
                  options={[
                    { value: "true", label: "Marcados" },
                    { value: "false", label: "Não marcados" },
                  ]}
                  value={scalarValue("falar_com_frila")}
                  onChange={(value) => setScalar("falar_com_frila", value)}
                />
              </div>
            </AccordionContent>
          </AccordionItem>
          {/* ---------------- Disponibilidade ---------------- */}
          <AccordionItem value="disponibilidade" className="border-b-0">
            <GroupTrigger filters={filters} group="disponibilidade" />
            {/* px/pt: o ring do chip ativo vaza 3px e o overflow-hidden do
                Accordion cortava a borda sem esse respiro. */}
            <AccordionContent className="px-1 pt-1">
              {disponibilidadeContent}
              <div className="mt-4">
                <FieldLabel>Open to Work no LinkedIn</FieldLabel>
                <SegmentedFilter
                  options={OPEN_TO_WORK_OPTIONS}
                  value={scalarValue("open_to_work")}
                  onChange={(value) => setScalar("open_to_work", value)}
                />
              </div>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </div>

      <div className="flex items-center justify-between gap-2 border-t bg-background px-4 py-2.5">
        <span className="flex items-center gap-1.5 text-sm text-muted-foreground">
          {isFetching && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
          {typeof resultCount === "number" && (
            <>
              <strong className="font-semibold text-foreground">
                {resultCount.toLocaleString("pt-BR")}
              </strong>
              {resultCount === 1 ? "resultado" : "resultados"}
            </>
          )}
        </span>
        <Button variant="outline" size="sm" onClick={onClearAll}>
          Limpar filtros
        </Button>
      </div>
    </aside>
  );
}

function GroupTrigger({
  filters,
  group,
}: {
  filters: FilterValues;
  group: GroupId;
}) {
  const active = countGroupActive(filters, group);
  return (
    <AccordionTrigger className="py-3 text-sm hover:no-underline">
      <span className="flex items-center gap-2">
        {GROUP_TITLE[group]}
        {active > 0 && (
          <Badge variant="secondary" className="h-5 px-1.5 text-[11px]">
            {active}
          </Badge>
        )}
      </span>
    </AccordionTrigger>
  );
}
